import { Layout } from "react-grid-layout";

import { clampUnits } from "@/features/admin/features/pages-management/utils/grid-resize-math";

/**
 * SE-corner resize: update one widget's size from a drag-start snapshot.
 * Neighbors are never adjusted — multi-widget exchange belongs to gutters.
 */
export function applySingleWidgetResize(
  snapshot: Layout[],
  containerId: string,
  w: number,
  h: number,
): Layout[] {
  return snapshot.map((item) => (item.i === containerId ? { ...item, w, h } : item));
}

/** Clamp pointer deltas to min/max bounds for a single-widget SE resize. */
export function computeSeResizeDimensions(
  startW: number,
  startH: number,
  deltaColumns: number,
  deltaRows: number,
  minW: number,
  minH: number,
  maxW: number,
): { w: number; h: number } {
  return {
    w: clampUnits(startW + deltaColumns, minW, maxW),
    h: Math.max(startH + deltaRows, minH),
  };
}

/** Label shown on the size overlay while resizing, e.g. "6 × 10". */
export function formatResizeSizeLabel(w: number, h: number): string {
  return `${w} × ${h}`;
}

/**
 * One controlled SE resize step: dimensions from the drag-start snapshot only.
 * Used instead of react-grid-layout's built-in resize (isResizable=false on the grid).
 */
export function applySeResizeStep(
  snapshot: Layout[],
  containerId: string,
  startW: number,
  startH: number,
  deltaColumns: number,
  deltaRows: number,
  minW: number,
  minH: number,
  maxW: number,
): { layout: Layout[]; w: number; h: number; sizeLabel: string } {
  const { w, h } = computeSeResizeDimensions(
    startW,
    startH,
    deltaColumns,
    deltaRows,
    minW,
    minH,
    maxW,
  );

  return {
    layout: applySingleWidgetResize(snapshot, containerId, w, h),
    w,
    h,
    sizeLabel: formatResizeSizeLabel(w, h),
  };
}
